import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  SquaresFour, BookOpen, ClipboardText,
  ChatCircleText, SunHorizon, SignOut, ForkKnife, Buildings,
  Swap, GraduationCap, FilmSlate, ShieldCheck, LockKey, EnvelopeSimple, CalendarBlank,
  UserGear, List, X, ListChecks, ChartBar, TreeStructure, Lightning, Robot,
} from "@phosphor-icons/react";
import { AlertsBell } from "./AlertsBell";
import { AlarmBanner } from "./AlarmBanner";
import { LiveEvents } from "./LiveEvents";
import { OfflineIndicator } from "./OfflineIndicator";

const ALL = ["admin", "gm", "manager", "supervisor", "staff"];
const NAV = [
  { to: "/developer-admin", label: "Platform Admin", icon: ShieldCheck, roles: ["developer"] },
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, roles: ["admin", "gm", "manager"] },
  { to: "/venues", label: "Venues & Team", icon: Buildings, roles: ["admin", "gm", "cluster", "regional", "manager"] },
  { to: "/briefing", label: "My Briefing", icon: SunHorizon, roles: ALL },
  { to: "/tasks", label: "Floor Tasks", icon: ClipboardText, roles: ALL },
  { to: "/checklists", label: "Checklists", icon: ListChecks, roles: [...ALL, "cluster", "regional"] },
  { to: "/compliance", label: "Compliance", icon: ChartBar, roles: ["admin", "gm", "cluster", "regional", "manager"], op: "view_compliance" },
  { to: "/flagged", label: "AI Flags", icon: Robot, roles: ["admin", "gm", "cluster", "regional", "manager"] },
  { to: "/organisation", label: "Organisation", icon: TreeStructure, roles: ["admin", "gm"] },
  { to: "/escalations", label: "Escalations", icon: Lightning, roles: ["admin", "gm"] },
  { to: "/roster", label: "Duty Roster", icon: CalendarBlank, roles: ALL },
  { to: "/handover", label: "Handovers", icon: Swap, roles: ["admin", "gm", "manager", "supervisor", "staff"] },
  { to: "/sops", label: "SOP Vault", icon: BookOpen, roles: ALL },
  { to: "/academy", label: "Academy", icon: GraduationCap, roles: [...ALL, "developer"] },
  { to: "/media", label: "Video Studio", icon: FilmSlate, roles: ["manager", "developer"] },
  { to: "/reviews", label: "Reviews", icon: ChatCircleText, roles: ["admin", "gm", "manager"] },
  { to: "/digest", label: "Weekly Digest", icon: EnvelopeSimple, roles: ["admin"] },
  { to: "/permissions", label: "Permissions", icon: UserGear, roles: ["admin", "gm"] },
  { to: "/account", label: "Security", icon: LockKey, roles: [...ALL, "developer"] },
];

export const FEATURE_MAP = {
  "/academy": "academy", "/media": "academy",
  "/sops": "sops",
  "/briefing": "briefings",
  "/reviews": "reviews",
  "/tasks": "tasks",
  "/checklists": "checklists",
  "/compliance": "checklists",
  "/flagged": "checklists",
  "/escalations": "checklists",
  "/handover": "audits",
  "/roster": "roster",
  "/digest": "analytics",
};

export function navItemsFor(user) {
  return NAV.filter((n) => n.roles.includes(user.role) && (!n.op || user.ops?.[n.op] !== false) &&
    (user.role === "developer" || !FEATURE_MAP[n.to] || user.features?.[FEATURE_MAP[n.to]] !== false));
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);

  const items = navItemsFor(user);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <aside className="hidden md:flex flex-col w-64 shrink-0 bg-white border-r border-slate-200 sticky top-0 h-screen">
        <div className="p-6 border-b border-slate-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center">
            <ForkKnife size={22} weight="duotone" className="text-emerald-400" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-lg leading-none tracking-tight">Practitality</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-bold mt-1">Ops Control</p>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl font-semibold text-sm tap-press ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
              <n.icon size={20} weight="duotone" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2">
            <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-heading font-extrabold text-sm">
              {user.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" data-testid="user-name">{user.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user.position || user.role_label || user.role}</p>
            </div>
            <button data-testid="logout-btn" onClick={doLogout}
              className="text-slate-400 hover:text-red-500 tap-press p-2">
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </aside>

      {drawer && (
        <div className="md:hidden fixed inset-0 z-[900]" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-slate-950/50" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white flex flex-col rise-in">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center">
                  <ForkKnife size={20} weight="duotone" className="text-emerald-400" />
                </div>
                <p className="font-heading font-extrabold text-lg">Practitality</p>
              </div>
              <button data-testid="mobile-drawer-close" onClick={() => setDrawer(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto">
              {items.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setDrawer(false)} data-testid={`mobile-nav-${n.to.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm tap-press ${
                      isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}>
                  <n.icon size={20} weight="duotone" />
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="p-4 border-t border-slate-200 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-heading font-extrabold text-sm shrink-0">
                {user.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{user.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user.position || user.role_label || user.role}</p>
              </div>
              <button data-testid="mobile-drawer-logout" onClick={doLogout} className="text-slate-400 hover:text-red-500 tap-press p-2"><SignOut size={20} /></button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <LiveEvents />
        <AlarmBanner />
        <header className="mobile-header md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button data-testid="mobile-menu-btn" onClick={() => setDrawer(true)} className="text-slate-700 p-2 -ml-2 tap-press"><List size={24} /></button>
            <p className="font-heading font-extrabold text-lg">Practitality</p>
          </div>
          <button data-testid="logout-btn-mobile" onClick={doLogout} className="text-slate-500 p-2"><SignOut size={22} /></button>
        </header>
        <main className="p-4 sm:p-8 lg:p-10 pb-28 md:pb-10 max-w-7xl mx-auto overflow-x-hidden">
          <div className="flex items-center justify-end gap-3 mb-6">
            <OfflineIndicator />
            {["admin", "gm", "cluster", "regional", "manager"].includes(user.role) && <AlertsBell />}
          </div>
          <Outlet />
        </main>
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 flex justify-around py-2">
          {items.slice(0, 5).map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-[10px] font-bold ${isActive ? "text-slate-900" : "text-slate-400"}`}>
              <n.icon size={24} weight="duotone" />
              {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
